import React, { useState, useRef, useEffect } from 'react';
import { Layout, ChevronDown, X } from 'lucide-react';

const DashboardHeader = ({
  projects,
  ships,
  loadingProjects,
  selectedProject,
  setSelectedProject,
  selectedShip,
  setSelectedShip,
  selectedYear,
  setSelectedYear,
  visibleCharts,
  setVisibleCharts
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  const chartOptions = [
    { id: 'sCurve', label: 'S-Curve' },
    { id: 'projectStatus', label: 'Project Status' },
    { id: 'criticalPath', label: 'Critical Path' },
    { id: 'financialOverview', label: 'Financial' },
    { id: 'projectTable', label: 'Project Table' },
    { id: 'physicalProgress', label: 'Progress' }
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleChart = (chartId) => {
    setVisibleCharts(prev => 
      prev.includes(chartId) 
        ? prev.filter(id => id !== chartId) 
        : [...prev, chartId]
    );
  };

  return (
    <div className="sticky top-0 z-30 w-full mb-6 rounded-xl bg-white/80 backdrop-blur-md border px-4 py-4">
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
        <div className="lg:min-w-[30%]">
          <h1 className="text-2xl font-bold text-indigo-900">Analytics Dashboard</h1>
          <p className="text-sm text-indigo-600">Project Performance Overview</p>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 w-full">
          {/* Chart Selector Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <label className="text-sm font-medium text-gray-700 mb-2">Select Section</label>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="w-full flex items-center justify-between px-3 py-2 bg-white border rounded-lg text-sm"
            >
              <div className="flex items-center gap-2">
                <Layout size={16} />
                <span>Sections</span>
              </div>
              <div className="flex items-center gap-1">
                {visibleCharts.length > 0 && (
                  <span className="px-2 py-0.5 text-xs bg-indigo-100 text-indigo-800 rounded-full">
                    {visibleCharts.length}
                  </span>
                )}
                <ChevronDown size={16} />
              </div>
            </button>

            {isDropdownOpen && (
              <div className="absolute z-10 mt-1 w-full bg-white rounded-lg shadow-lg border">
                <div className="p-2 border-b flex justify-between">
                  <div className="flex gap-2">
                    <button
                      onClick={() => setVisibleCharts(chartOptions.map(o => o.id))}
                      className="text-xs text-indigo-600"
                    >
                      Select All
                    </button>
                    <button
                      onClick={() => setVisibleCharts([])}
                      className="text-xs text-indigo-600"
                    >
                      Deselect All
                    </button>
                  </div>
                  <button onClick={() => setIsDropdownOpen(false)}>
                    <X size={16} />
                  </button>
                </div>
                <div className="max-h-60 overflow-y-auto">
                  {chartOptions.map(option => (
                    <label key={option.id} className="flex items-center px-3 py-2 hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={visibleCharts.includes(option.id)}
                        onChange={() => toggleChart(option.id)}
                        className="h-4 w-4 text-indigo-600"
                      />
                      <span className="ml-2 text-sm">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Project Selector */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1">Select Project</label>
            <select
              value={selectedProject}
              onChange={e => {
                setSelectedProject(e.target.value);
                setSelectedShip('');
              }}
              className="w-full text-sm border rounded-lg px-3 py-2 bg-white"
              disabled={loadingProjects}
            >
              <option value="">All Projects</option>
              {projects.map(proj => (
                <option key={proj.id} value={proj.id}>{proj.name}</option>
              ))}
            </select>
          </div>

          {/* Ship Selector */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1">Select Ship</label>
            <select
              value={selectedShip}
              onChange={e => setSelectedShip(e.target.value)}
              className="w-full text-sm border rounded-lg px-3 py-2 bg-white"
              disabled={!selectedProject || ships.length === 0}
            >
              <option value="">All Ships</option>
              {ships.map(ship => (
                <option key={ship.id} value={ship.id}>{ship.name}</option>
              ))}
            </select>
          </div>

          {/* Year Selector */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1">Select Year</label>
            <select
              value={selectedYear}
              onChange={e => setSelectedYear(e.target.value)}
              className="w-full text-sm border rounded-lg px-3 py-2 bg-white"
            >
              <option value="">All Years</option>
              {[2023, 2024, 2025, 2026].map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardHeader;